// src/components/dashboards/add_patient_v2/components/FolderLevelPickerDialog.tsx
// -----------------------------------------------------------------------------
// FolderLevelPickerDialog
//
// Asks the user where the patient boundary sits when a dropped folder
// contains subfolders. Two options today:
//   - depth 0 → the dropped root is the patient (1 patient, all files)
//   - depth 1 → each immediate subfolder is a patient (N patients)
//
// Triggered from AddPatientFormV2 when a dropped folder
// whose `subfolders` array is non-empty.
//
// Joan, 2026-06-04: switched to the shared AthenaDialog *dark* variant so the
// modal reads the same as PatientEnvironmentsModal / ExternalIdEditor.
// Interactive controls (radios, chips, buttons) demand the dark treatment per
// the rule in AthenaDialogStyles.ts. The previous default Paper inherited the
// app's light theme paper but the title was missing contrast tokens, so text
// rendered against an off-white-on-blue mix and was effectively invisible.
// -----------------------------------------------------------------------------

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import { Folder as FolderIcon } from "@mui/icons-material";
import React, { useMemo, useState } from "react";

import { useTranslation } from "../../../../i18n";
import { FolderNode, flattenFolderFiles } from "./AddPatientsDropzone";
import {
  athenaColors,
  athenaDarkActionsSx,
  athenaDarkContentSx,
  athenaDarkPaperSx,
  athenaDarkPrimaryButtonSx,
  athenaDarkSecondaryButtonSx,
  athenaDarkTitleSx,
} from "../../../layout/AthenaDialogStyles";

export interface FolderLevelPickerDialogProps {
  open: boolean;
  /** The root folder that was dropped (the one with subfolders). */
  rootFolder: FolderNode | null;
  onCancel: () => void;
  /** User picked depth 0 — fire one card with all files. */
  onChooseRoot: (folder: FolderNode) => void;
  /** User picked depth 1 — fire one card per subfolder. Loose root files,
   *  if any, are returned separately so the caller can decide what to do
   *  with them (we add them as one extra card by default). */
  onChooseSubfolders: (
    subfolders: FolderNode[],
    looseRootFiles: File[],
  ) => void;
}

const FolderLevelPickerDialog: React.FC<FolderLevelPickerDialogProps> = ({
  open,
  rootFolder,
  onCancel,
  onChooseRoot,
  onChooseSubfolders,
}) => {
  const { t } = useTranslation();
  const [choice, setChoice] = useState<"root" | "subfolders">("subfolders");

  const stats = useMemo(() => {
    if (!rootFolder) {
      return {
        rootFileCount: 0,
        subfolderCount: 0,
        totalFileCount: 0,
        looseFileCount: 0,
      };
    }
    const totalFileCount = flattenFolderFiles(rootFolder).length;
    return {
      rootFileCount: totalFileCount,
      subfolderCount: rootFolder.subfolders.length,
      totalFileCount,
      looseFileCount: rootFolder.files.length,
    };
  }, [rootFolder]);

  if (!rootFolder) return null;

  const handleConfirm = () => {
    if (choice === "root") {
      onChooseRoot(rootFolder);
    } else {
      onChooseSubfolders(rootFolder.subfolders, rootFolder.files);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: athenaDarkPaperSx }}
    >
      <DialogTitle sx={athenaDarkTitleSx}>
        <FolderIcon fontSize="small" />
        {t("patients_v2.folder_picker_title", "Folder structure detected")}
      </DialogTitle>
      <DialogContent sx={athenaDarkContentSx}>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t(
            "patients_v2.folder_picker_body",
            "We found subfolders inside ",
          )}
          <strong>{rootFolder.name}</strong>
          {t(
            "patients_v2.folder_picker_body_2",
            ". How should we split this into patients?",
          )}
        </Typography>

        <RadioGroup
          value={choice}
          onChange={(_e, v) => setChoice(v as "root" | "subfolders")}
        >
          <FormControlLabel
            value="subfolders"
            control={<Radio />}
            sx={{ alignItems: "flex-start", mb: 1.5, mt: 0.5 }}
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t(
                    "patients_v2.folder_picker_subfolders",
                    "{count} patients — one per subfolder",
                  ).replace("{count}", String(stats.subfolderCount))}
                </Typography>
                <Stack
                  direction="row"
                  spacing={0.75}
                  sx={{
                    flexWrap: "wrap",
                    mt: 0.5,
                    gap: 0.5,
                    rowGap: 0.5,
                  }}
                >
                  {rootFolder.subfolders.slice(0, 6).map((sub) => (
                    <Box
                      key={sub.name}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.4,
                        px: 0.9,
                        py: 0.3,
                        borderRadius: 1,
                        // Periwinkle-tinted chip on the dark gradient — the
                        // prior `primary.main * 0.08` resolved to a near-
                        // transparent rectangle on dark, hiding the label.
                        bgcolor: alpha(athenaColors.periwinkle, 0.18),
                        border: `1px solid ${alpha(
                          athenaColors.periwinkle,
                          0.35,
                        )}`,
                        fontSize: "0.72rem",
                        color: athenaColors.white,
                      }}
                    >
                      <FolderIcon
                        sx={{ fontSize: 11, color: athenaColors.periwinkle }}
                      />
                      <span>{sub.name}</span>
                      <span style={{ opacity: 0.7 }}>
                        ({flattenFolderFiles(sub).length})
                      </span>
                    </Box>
                  ))}
                  {rootFolder.subfolders.length > 6 && (
                    <Typography
                      variant="caption"
                      sx={{ alignSelf: "center" }}
                    >
                      {t("patients_v2.folder_picker_more", "+{count} more").replace(
                        "{count}",
                        String(rootFolder.subfolders.length - 6),
                      )}
                    </Typography>
                  )}
                </Stack>
                {stats.looseFileCount > 0 && (
                  <Typography
                    variant="caption"
                    sx={{ display: "block", mt: 0.75 }}
                  >
                    {t(
                      "patients_v2.folder_picker_loose_warning",
                      "+1 patient for {count} loose file(s) directly in {root}/",
                    )
                      .replace("{count}", String(stats.looseFileCount))
                      .replace("{root}", rootFolder.name)}
                  </Typography>
                )}
              </Box>
            }
          />

          <FormControlLabel
            value="root"
            control={<Radio />}
            sx={{ alignItems: "flex-start" }}
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t(
                    "patients_v2.folder_picker_root",
                    "1 patient — {root} (all {count} files)",
                  )
                    .replace("{root}", rootFolder.name)
                    .replace("{count}", String(stats.totalFileCount))}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ display: "block", mt: 0.25 }}
                >
                  {t(
                    "patients_v2.folder_picker_root_hint",
                    "Flatten the whole folder into a single patient. Pick this if {root} represents one person.",
                  ).replace("{root}", rootFolder.name)}
                </Typography>
              </Box>
            }
          />
        </RadioGroup>
      </DialogContent>
      <DialogActions sx={athenaDarkActionsSx}>
        <Button onClick={onCancel} sx={athenaDarkSecondaryButtonSx}>
          {t("action.cancel", "Cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          sx={athenaDarkPrimaryButtonSx}
        >
          {t("patients_v2.folder_picker_confirm", "Continue")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FolderLevelPickerDialog;
